/**
 * Zod validation schemas for Product Variant types
 * Updated for new API: collaborator_ids array and prices array support
 */

import { z } from 'zod';
import { createProductPriceSchema } from './prices';

export const productVariantSchema = z.object({
  id: z.string(),
  product_id: z.string(),
  variant_name: z.string(),
  quantity: z.string(),
  pack_size: z.string(),
  sku: z.string().optional(),
  barcode: z.string().optional(),
  collaborator_ids: z.array(z.string()), // Changed from single collaborator_id
  brand_name: z.string().optional(),
  hsn_code: z.string().length(8, 'HSN code must be exactly 8 digits'),
  gst_rate: z.number().min(0).max(100, 'GST rate must be between 0 and 100'),
  prices: z.array(z.any()).optional(), // Prices array (using any for now, can be refined)
  images: z.array(z.string()).optional(),
  dosage_instructions: z.string().optional(),
  usage_details: z.string().optional(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});

export const createProductVariantSchema = z.object({
  variant_name: z.string().min(1, 'Variant name is required'),
  quantity: z.string().min(1, 'Quantity is required'),
  pack_size: z.string().min(1, 'Pack size is required'),
  barcode: z.string().optional(),
  collaborator_ids: z.array(z.string()).min(1, 'At least one collaborator is required'),
  brand_name: z.string().optional(),
  hsn_code: z.string().regex(/^\d{8}$/, 'HSN code must be exactly 8 digits'),
  gst_rate: z.number().min(0).max(100, 'GST rate must be between 0 and 100'),
  prices: z.array(createProductPriceSchema.omit({ variant_id: true })).optional(), // Prices array (variant_id will be set by backend)
  images: z.array(z.string()).optional(),
  dosage_instructions: z.string().optional(),
  usage_details: z.string().optional(),
});

// Alias for compatibility
export const CreateProductVariantSchema = createProductVariantSchema;

export const updateProductVariantSchema = createProductVariantSchema.partial();

export type ProductVariant = z.infer<typeof productVariantSchema>;
export type CreateProductVariant = z.infer<typeof createProductVariantSchema>;
export type CreateProductVariantRequest = CreateProductVariant;
export type UpdateProductVariant = z.infer<typeof updateProductVariantSchema>;
